import { Link } from 'react-router-dom';
import { AlertCircle, MessageCircle } from 'lucide-react';
import { PageShell } from '@/components/layout/PageShell';
import { EmptyState } from '@/components/ui/EmptyState';
import { formatDate } from '@/lib/format';
import { useChatThreads, threadTitle } from '../hooks/useChatThreads';
import { useAuth } from '@/hooks/useAuth';

/** Inbox de conversas do MYND Farms — lista todas as threads do usuário. */
export function ChatInbox() {
  const { user } = useAuth();
  const { threads, loading, error } = useChatThreads();

  return (
    <PageShell title="Conversas" back="/farms" maxWidth="md">
      {loading && <p className="text-sm text-ink-muted text-center py-8">Carregando conversas…</p>}

      {!loading && error && (
        <div className="flex items-start gap-2 rounded-xl bg-negative/10 border border-negative/20 px-3 py-2 text-xs text-negative">
          <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
          <span>Erro ao carregar conversas: {error}</span>
        </div>
      )}

      {!loading && !error && threads.length === 0 && (
        <EmptyState
          icon={MessageCircle}
          title="Nenhuma conversa ainda"
          description="Quando você contatar uma farm (ou receber uma mensagem), a conversa aparecerá aqui."
        />
      )}

      {!loading && !error && threads.length > 0 && (
        <div className="card divide-y divide-border">
          {threads.map((t) => {
            const title = threadTitle(t, user?.id);
            const iAmFarmOwner = !!user && t.farm_owner_id === user.id;
            const isMine = t.last_sender_id === user?.id;
            const preview = t.last_message
              ? `${isMine ? 'Você: ' : ''}${t.last_message}`
              : 'Nenhuma mensagem ainda';
            return (
              <Link
                key={t.thread_id}
                to={`/farms/chat/${t.thread_id}`}
                className="flex items-center gap-3 px-4 py-3 hover:bg-surface-subtle transition-colors"
              >
                <div className="w-10 h-10 rounded-full bg-brand-100 text-brand-700 flex items-center justify-center shrink-0">
                  <MessageCircle className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <p className={`text-sm truncate ${t.unread_count > 0 ? 'font-bold text-ink' : 'font-medium text-ink'}`}>
                      {title}
                      {iAmFarmOwner && t.farm_name && (
                        <span className="text-xs font-normal text-ink-muted"> · cliente da {t.farm_name}</span>
                      )}
                    </p>
                    {t.last_message_at && (
                      <span className="text-[11px] text-ink-soft shrink-0">{formatDate(t.last_message_at)}</span>
                    )}
                  </div>
                  <p className={`text-xs truncate mt-0.5 ${t.unread_count > 0 ? 'text-ink font-medium' : 'text-ink-muted'}`}>
                    {preview}
                  </p>
                </div>
                {t.unread_count > 0 && (
                  <span className="w-5 h-5 rounded-full bg-brand-700 text-white text-[11px] font-bold flex items-center justify-center shrink-0">
                    {t.unread_count > 9 ? '9+' : t.unread_count}
                  </span>
                )}
              </Link>
            );
          })}
        </div>
      )}
    </PageShell>
  );
}
